/* @jsx h */
import { computed, defineComponent, h, onBeforeUnmount, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { localDate } from "../WorkDay/shared/dates";
import type { LinkView } from "./types";

type Props = { token: string; view: LinkView; onChanged: () => void };

const time = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

// Clock in/out from the personal link. Managers review every entry under
// Rota → Hours. See docs/plans/workday/staff_planning.md (phase 5).
export const ClockCard = defineComponent<Props>({
  name: "ClockCard",
  props: ["token", "view", "onChanged"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const busy = ref(false);
    const now = ref(Date.now());
    const timer = setInterval(() => (now.value = Date.now()), 30_000);
    onBeforeUnmount(() => clearInterval(timer));

    const today = localDate();
    const todayShifts = computed(() => props.view.shifts.filter((s) => s.shift_date === today));

    const elapsed = computed(() => {
      if (!props.view.clock) return "";
      const mins = Math.max(0, Math.floor((now.value - new Date(props.view.clock.clock_in).getTime()) / 60_000));
      return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
    });

    const act = async (action: "in" | "out") => {
      busy.value = true;
      try {
        await trpc.teamLink.clock.mutate({ token: props.token, action });
        toast.showToast(t(action === "in" ? "teamlink.page.clocked_in" : "teamlink.page.clocked_out"), { type: "success" });
        props.onChanged();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("teamlink.page.failed"), { type: "error" });
      } finally {
        busy.value = false;
      }
    };

    return () => {
      const clock = props.view.clock;
      return (
        <section class={["rounded-2xl p-5", clock ? "bg-success/15" : "bg-base-100"].join(" ")}>
          <div class="flex items-center justify-between gap-4">
            <div class="min-w-0">
              <div class="text-sm text-base-content/60">
                {clock ? t("teamlink.page.on_the_clock_since").replace("{time}", time(clock.clock_in)) : t("teamlink.page.clock_title")}
              </div>
              {clock ? (
                <div class="text-4xl font-bold tabular-nums">{elapsed.value}</div>
              ) : todayShifts.value.length ? (
                <div class="mt-1 text-lg font-semibold">
                  {todayShifts.value.map((s) => `${s.start_time.slice(0, 5)}–${s.end_time.slice(0, 5)}`).join(" · ")}
                </div>
              ) : (
                <div class="mt-1 text-base-content/70">{t("teamlink.page.no_shift_today")}</div>
              )}
            </div>
            <button
              class={["btn btn-lg min-w-32", clock ? "btn-neutral" : "btn-primary"].join(" ")}
              disabled={busy.value}
              onClick={() => act(clock ? "out" : "in")}
            >
              {busy.value ? <span class="loading loading-spinner" /> : <span class="material-symbols-outlined">{clock ? "logout" : "login"}</span>}
              {t(clock ? "teamlink.page.clock_out" : "teamlink.page.clock_in")}
            </button>
          </div>
        </section>
      );
    };
  },
});
